import type { TFunction } from 'i18next';
import { DiscountType, formatMoney } from '../coupons/couponTypes';

export type DiscountScope = 'ALL_ITEMS' | 'SELECTED_ITEMS';
export type DiscountStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'DISABLED' | 'BUDGET_EXHAUSTED';
export type Weekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export const WEEKDAYS: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

// Godziny jak z backendu ("12:00:00") albo z inputu type="time" ("12:00"); oba null = caly dzien.
export interface DiscountTimeWindow {
  weekday: Weekday;
  startTime: string | null;
  endTime: string | null;
}

export interface Discount {
  id: number;
  locationId: number;
  locationName?: string;
  name: string;
  discountType: DiscountType;
  discountValue: number;
  scope: DiscountScope;
  inventoryItemIds: number[];
  minOrderAmount: number;
  startDate: string | null;
  endDate: string | null;
  timeWindows: DiscountTimeWindow[];
  isActive: boolean;
  budget: number | null;
  spentAmount: number;
  remainingBudget: number | null;
  disabledReason: 'BUDGET_EXHAUSTED' | null;
  budgetExhaustedAt: string | null;
  firstOrderOnly: boolean;
  perCustomerLimit: number | null;
  status: DiscountStatus;
  locationTimeZone: string | null;
}

export const hhmm = (time: string | null) => (time ? time.slice(0, 5) : '');

export const formatDiscountValue = (
  discount: Pick<Discount, 'discountType' | 'discountValue'>,
  currency = 'PLN',
) =>
  discount.discountType === 'PERCENT'
    ? `−${discount.discountValue}%`
    : `−${formatMoney(discount.discountValue, currency)}`;

const formatDate = (iso: string) => {
  const [, month, day] = iso.split('-');
  return `${day}.${month}`;
};

const formatDateRange = (discount: Pick<Discount, 'startDate' | 'endDate'>, t: TFunction) => {
  const { startDate, endDate } = discount;
  if (startDate && endDate) return `${formatDate(startDate)}–${formatDate(endDate)}`;
  if (startDate) return t('discounts.schedule.from', { date: formatDate(startDate) });
  if (endDate) return t('discounts.schedule.until', { date: formatDate(endDate) });
  return null;
};

const windowHours = (window: DiscountTimeWindow, t: TFunction) =>
  window.startTime && window.endTime
    ? `${hhmm(window.startTime)}–${hhmm(window.endTime)}`
    : t('discounts.schedule.allDay');

/**
 * Skrot harmonogramu do listy, np. "01.10–31.10 · Wt, Czw 13:00–16:00 · Pt 12:00–14:00".
 * Dni z identycznymi godzinami sa grupowane.
 */
export const formatSchedule = (
  discount: Pick<Discount, 'startDate' | 'endDate' | 'timeWindows'>,
  t: TFunction,
) => {
  const parts: string[] = [];
  const dates = formatDateRange(discount, t);
  if (dates) parts.push(dates);

  if (discount.timeWindows.length > 0) {
    const daysByHours = new Map<string, Weekday[]>();
    [...discount.timeWindows]
      .sort((a, b) => WEEKDAYS.indexOf(a.weekday) - WEEKDAYS.indexOf(b.weekday))
      .forEach((window) => {
        const hours = windowHours(window, t);
        daysByHours.set(hours, [...(daysByHours.get(hours) ?? []), window.weekday]);
      });

    daysByHours.forEach((days, hours) => {
      const uniqueDays = days.filter((day, index) => days.indexOf(day) === index);
      parts.push(
        `${uniqueDays.map((day) => t(`discounts.weekdaysShort.${day}`)).join(', ')} ${hours}`,
      );
    });
  } else if (!dates) {
    parts.push(t('discounts.schedule.always'));
  }

  return parts.join(' · ');
};

export const budgetUsagePercent = (discount: Pick<Discount, 'budget' | 'spentAmount'>) =>
  discount.budget ? Math.min(100, Math.round((discount.spentAmount / discount.budget) * 100)) : 0;

// Tylko pola DiscountRequest - nigdy calego rekordu z listy (spentAmount, status itp.
// sa wylacznie do odczytu i nie maja trafiac do zapisu).
export type DiscountPayload = Pick<
  Discount,
  | 'locationId'
  | 'name'
  | 'discountType'
  | 'discountValue'
  | 'scope'
  | 'inventoryItemIds'
  | 'minOrderAmount'
  | 'startDate'
  | 'endDate'
  | 'timeWindows'
  | 'budget'
  | 'firstOrderOnly'
  | 'perCustomerLimit'
  | 'isActive'
>;

export const toDiscountPayload = (discount: DiscountPayload): DiscountPayload => ({
  locationId: discount.locationId,
  name: discount.name,
  discountType: discount.discountType,
  discountValue: discount.discountValue,
  scope: discount.scope,
  inventoryItemIds: discount.scope === 'SELECTED_ITEMS' ? discount.inventoryItemIds : [],
  minOrderAmount: discount.minOrderAmount,
  startDate: discount.startDate || null,
  endDate: discount.endDate || null,
  timeWindows: discount.timeWindows.map((window) => ({
    weekday: window.weekday,
    startTime: window.startTime ? hhmm(window.startTime) : null,
    endTime: window.endTime ? hhmm(window.endTime) : null,
  })),
  budget: discount.budget,
  // Rabat sprzed ograniczen dla klienta moze nie miec tych pol - wtedy zapis zostawia go
  // bez ograniczen, tak jak byl.
  firstOrderOnly: discount.firstOrderOnly ?? false,
  perCustomerLimit: discount.perCustomerLimit ?? null,
  isActive: discount.isActive,
});

const MINUTES_PER_DAY = 24 * 60;
const MINUTES_PER_WEEK = 7 * MINUTES_PER_DAY;

const toMinutes = (time: string) => {
  const [hours, minutes] = hhmm(time).split(':').map(Number);
  return hours * 60 + minutes;
};

// Przedzial musi miec obie godziny (rozne) albo zadnej (caly dzien) - jak na backendzie.
export const isValidWindow = (window: DiscountTimeWindow) =>
  (window.startTime == null && window.endTime == null) ||
  (Boolean(window.startTime) &&
    Boolean(window.endTime) &&
    hhmm(window.startTime) !== hhmm(window.endTime));

/**
 * Czy przedzialy zachodza na siebie - na osi minut tygodnia, z oknami przez polnoc
 * i z niedzieli na poniedzialek. Ta sama regula co DiscountService#validateWindows.
 */
export const hasOverlappingWindows = (windows: DiscountTimeWindow[]) => {
  const intervals: [number, number][] = [];

  windows.filter(isValidWindow).forEach((window) => {
    const dayStart = WEEKDAYS.indexOf(window.weekday) * MINUTES_PER_DAY;
    const allDay = window.startTime == null;
    const start = dayStart + (allDay ? 0 : toMinutes(window.startTime as string));
    const length = allDay
      ? MINUTES_PER_DAY
      : (((toMinutes(window.endTime as string) - toMinutes(window.startTime as string)) %
          MINUTES_PER_DAY) +
          MINUTES_PER_DAY) %
        MINUTES_PER_DAY;
    const end = start + length;

    if (end > MINUTES_PER_WEEK) {
      intervals.push([start, MINUTES_PER_WEEK], [0, end - MINUTES_PER_WEEK]);
    } else {
      intervals.push([start, end]);
    }
  });

  return intervals.some(([startA, endA], i) =>
    intervals.some(([startB, endB], j) => j > i && startA < endB && startB < endA),
  );
};
